(ns common.widgets.data-grid
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Checkbox$default" :as Checkbox]
            ["@mui/material/ClickAwayListener$default" :as ClickAwayListener]
            ["@mui/material/FormControlLabel$default" :as FormControlLabel]
            ["@mui/material/Link$default" :as Link]
            ["@mui/material/ListItemText$default" :as ListItemText]
            ["@mui/material/MenuItem$default" :as MenuItem]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Popper$default" :as Popper]
            ["@mui/material/Radio$default" :as Radio]
            ["@mui/material/Select$default" :as Select]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium"
             :refer [GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD
                     GridCell
                     gridFilterModelSelector
                     GridRow
                     GridToolbarQuickFilter
                     isAutogeneratedRow
                     useGridApiContext
                     useGridSelector]]
            ["src/svg/controls/filter.svg$default" :as Filter]
            [cljs-bean.core :refer [->clj ->js bean?]]
            [clojure.data :as data]
            [clojure.string :as str]
            [common.ocp.view :refer [defocp ocp]]
            [common.queries :refer [use-infinite-query]]
            [common.util :as util :refer [spread-obj]]
            [common.widgets.daterange-picker :as picker]
            [common.widgets.form-field :refer [form-field]]
            [goog.object :as gobject]
            [mate.core :as mc]
            [medley.core :refer [deep-merge]]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defhook defui]]))

(defui grid-toolbar
  "For use with :slots :toolbar

  Quick filter search bar on the left corner, and the
  optional children are placed after that."
  [{:keys [children sx hide-quick-filter quick-filter-props] :as props
    :or {hide-quick-filter false}}]
  ($ Stack
     (merge {:direction "row"
             :alignItems "stretch"
             :gap 1.5
             :sx (util/spread-obj
                  #js {:py 1
                       :justifyContent "space-between"
                       ".MuiFormControl-root" #js {:pb 0}}
                  sx)}
            (dissoc props :children :sx :hide-quick-filter :quick-filter-props))
     (when-not hide-quick-filter
       ($ GridToolbarQuickFilter quick-filter-props))
     children))

(def grid-toolbar-outlined-input
  "For use with :slotProps :baseTextField

  Changes the TextField style to follow the design"
  #js {:variant "outlined"
       :size "small"})

(defhook use-filter-model
  "Returns the data-grid's filter-model and a function to update it.
   Can only be used inside a data-grid."
  []
  (let [api-ref (useGridApiContext)
        filter-model (useGridSelector api-ref gridFilterModelSelector)
        set-filter-model (fn [filter-model]
                           (when-let [api (.-current api-ref)]
                             (.setFilterModel api filter-model)))]
    [filter-model set-filter-model]))

(defn replace-grouped-field-and-remove-duplicates [^js items group-model]
  (->> (->clj items)
       (reduce (fn [acc item]
                 (let [item (update item :field #(if (= % GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD)
                                                   (first group-model) %))]
                   (assoc acc (:field item) item)))
               {})
       vals
       (into [])
       clj->js))

(defhook use-datagrid-models [{:keys [^js api-ref]}]
  (let [[models set-models] (uix/use-state {:pagination-model nil
                                            :sort-model nil
                                            :filter-model nil
                                            :group-model nil})
        subscribeEvent (gobject/get (.-current api-ref) "subscribeEvent")]
    (uix/use-effect (fn []
                      (when subscribeEvent
                        (subscribeEvent "stateChange"
                                        (fn [^js state]
                                          (let [group-model (.. state -rowGrouping -model)
                                                pagination-model (.. state -pagination -paginationModel)
                                                sort-model (-> (.. state -sorting -sortModel)
                                                               (replace-grouped-field-and-remove-duplicates group-model))
                                                filter-model (.. state -filter -filterModel)]
                                            (set-models {:pagination-model pagination-model
                                                         :sort-model sort-model
                                                         :filter-model filter-model
                                                         :group-model group-model}))))))
                    [subscribeEvent])
    models))

(def WithDynamicPropsRow
  (uix/as-react
   (fn [{:keys [get-props-fn] :as props}]
     ($ GridRow (-> (dissoc props :get-props-fn)
                    (merge (get-props-fn props)))))))

(def WithDynamicPropsCell
  (uix/as-react
   (fn [{:keys [get-props-fn] :as props}]
     ($ GridCell (-> (dissoc props :get-props-fn)
                     (merge (get-props-fn props)))))))

(def WithLinkCell
  (uix/as-react
   (fn [{:keys [href-fn ^js column rowId] :as props}]
     (let [api (.-current (useGridApiContext))]
       (if-not (or (= (.-field column) "__check__")
                   (= (.-type column) "actions"))
         (let [row (.. api (getRow rowId))]
           ($ Link {:href (href-fn row)
                    :color "text.primary"
                    :underline "none"}
              ($ GridCell (dissoc props :href-fn))))
         ($ GridCell (dissoc props :href-fn)))))))

(defn default-transform [page]
  (->> page :data (map :data)))

(defhook use-server-table-props [{:keys [initial-pagination-model
                                         initial-sort-model
                                         initial-filter-model
                                         table-query-fn
                                         transform-response-fn
                                         disable-default-transform
                                         get-row-count-fn
                                         prevent-loading-on-refetch
                                         row-grouping-model
                                         enable-infinite-scroll]
                                  :or {initial-pagination-model #js {:page 0 :pageSize 8}
                                       initial-sort-model #js []
                                       initial-filter-model #js {:items #js []}
                                       transform-response-fn identity
                                       get-row-count-fn :total}}]
  (let [[^js pagination-model set-pagination-model] (uix/use-state initial-pagination-model)
        [sort-model set-sort-model] (uix/use-state initial-sort-model)
        [filter-model set-filter-model] (uix/use-state initial-filter-model)

        {:keys [data fetchNextPage isFetching isRefetching]}
        (use-infinite-query
         (table-query-fn {:pagination-model pagination-model
                          :sort-model sort-model
                          :filter-model filter-model}))

        ;; get the original js data behind `data` potentially wrapped in `Bean` to use it in
        ;; dependency arrays (otherwise it would be a different `Bean` object on every render)
        stable-data (cond-> data
                      (bean? data) ->js)
        pages (uix/use-memo (fn [] (-> stable-data ->clj :pages vec)) [stable-data])
        row-count (uix/use-memo #(or (-> pages first get-row-count-fn) 0)
                                [pages get-row-count-fn])
        transform-page (uix/use-callback
                        (fn [page]
                          (cond-> page
                            (not disable-default-transform) default-transform
                            true transform-response-fn))
                        [disable-default-transform default-transform transform-response-fn])
        rows-for-current-page (uix/use-memo
                               ;; Using `get` here instead of `nth` because if BE returns an
                               ;; error for the next page in the table, `nth` throws an out
                               ;; of bounds exception, `get` returns `nil`, so instead of a
                               ;; crash page we get an empty table.
                               #(-> pages
                                    (get (.-page pagination-model))
                                    transform-page
                                    to-array)
                               [pages transform-page pagination-model])

        on-pagination-model-change (uix/use-callback
                                    (fn [model]
                                      (when (and
                                             ;; Loading finished
                                             (not isFetching)
                                             ;; Latest loaded page still had rows
                                             (seq rows-for-current-page))
                                        (when (<= (count pages) (.-page model))
                                          (fetchNextPage))
                                        (set-pagination-model model)))
                                    [pages fetchNextPage isFetching rows-for-current-page])

        ;; Transforms the sort/filter model by updating the :field values
        ;; to correct grouped field names (from "__row_group_by_columns_group__" to the actual field name),
        ;; and removes duplicate items that have the same :field value.
        fix-grouped-filter-fields (uix/use-callback
                                   (fn [^js filter-model]
                                     (let [items (replace-grouped-field-and-remove-duplicates
                                                  (.-items filter-model)
                                                  row-grouping-model)]
                                       (util/merge-objects filter-model #js {:items items})))
                                   [row-grouping-model])

        fix-grouped-sort-field (uix/use-callback
                                (fn [^js sort-model]
                                  (replace-grouped-field-and-remove-duplicates
                                   sort-model
                                   row-grouping-model))
                                [row-grouping-model])

        on-sort-model-change (uix/use-callback
                              (fn [sort-model]
                                (set-pagination-model (-> pagination-model ->clj (assoc :page 0) ->js))
                                (set-sort-model (if (seq row-grouping-model)
                                                  (fix-grouped-sort-field sort-model)
                                                  sort-model)))
                              [pagination-model row-grouping-model fix-grouped-sort-field])

        on-filter-model-change (uix/use-callback
                                (fn [filter-model]
                                  (set-pagination-model (-> pagination-model ->clj (assoc :page 0) ->js))
                                  (set-filter-model (if (seq row-grouping-model)
                                                      (fix-grouped-filter-fields filter-model)
                                                      filter-model)))
                                [pagination-model row-grouping-model fix-grouped-filter-fields])

        [rows set-rows] (uix/use-state #js [])

        on-rows-scroll-end (uix/use-callback
                            (fn []
                              (on-pagination-model-change
                               (util/merge-objects
                                pagination-model
                                #js {:page (inc (.-page pagination-model))})))
                            [rows row-count pagination-model on-pagination-model-change])]
    (uix/use-effect
     (fn []
       (when-not isFetching
         (if enable-infinite-scroll
           (set-rows (to-array (mapcat transform-page pages)))
           (set-rows rows-for-current-page))))
     [isFetching pagination-model enable-infinite-scroll rows-for-current-page transform-page pages])
    (merge
     {:loading (and isFetching (not (and prevent-loading-on-refetch isRefetching)))
      :rows rows
      :rowCount row-count

      :keepNonExistentRowsSelected true
      :sortingModel sort-model
      :sortingMode "server"
      :onSortModelChange on-sort-model-change

      :filterModel filter-model
      :filterMode "server"
      :onFilterModelChange on-filter-model-change
      :pagination (not enable-infinite-scroll)
      :paginationMode "server"
      :paginationModel pagination-model
      :onPaginationModelChange on-pagination-model-change
      :pageSizeOptions #js [(.-pageSize pagination-model)]}
     (when enable-infinite-scroll
       {:onRowsScrollEnd on-rows-scroll-end}))))

(defhook use-editing [{:keys [^js api-ref user-is-editing? pagination-model loading
                              initial-diff on-discard on-save on-update set-rows]}]
  (let [initial-unsaved-changes {:rowsBeforeChange {}
                                 :diff {:add []
                                        :delete #{}
                                        :change {}}}

        [unsaved-changes set-unsaved-changes] (uix/use-reducer
                                               (fn [state {:keys [action path value update-fn]}]
                                                 (case action
                                                   :reset initial-unsaved-changes
                                                   (if (seq path)
                                                     (if update-fn
                                                       (update-in state path update-fn)
                                                       (assoc-in state path value))
                                                     (if update-fn
                                                       (update-fn state)
                                                       value))))

                                               (cond-> initial-unsaved-changes
                                                 initial-diff (assoc :diff initial-diff)))

        process-row-update (uix/use-callback
                            (fn [^js new-row ^js old-row]
                              (let [[_ only-in-new _] (data/diff (->clj old-row) (->clj new-row))
                                    row-id (.-id new-row)
                                    row-state-updater (fn [rows]
                                                        (.map rows (fn [row]
                                                                     (if (= (.-id row) (.-id new-row))
                                                                       (util/merge-objects row new-row)
                                                                       row))))
                                    update-added-row (fn []
                                                       (set-rows row-state-updater)
                                                       (set-unsaved-changes {:path [:diff :add]
                                                                             :update-fn (fn [rows]
                                                                                          (mapv (fn [row]
                                                                                                  (if (= (:id row) row-id)
                                                                                                    (merge row (->clj new-row))
                                                                                                    row))
                                                                                                rows))}))
                                    update-existing-row (fn []
                                                          (set-rows row-state-updater)

                                                          (set-unsaved-changes {:path [:diff :change (keyword (str row-id))]
                                                                                :update-fn #(merge % only-in-new)})

                                                          (set-unsaved-changes {:path [:diff :delete]
                                                                                :update-fn #(disj % row-id)})

                                                          (set-unsaved-changes {:path [:rowsBeforeChange (keyword (str row-id))]
                                                                                :update-fn (fn [row]
                                                                                             (or row old-row))}))]
                                (when only-in-new
                                  (if (.-isNew new-row)
                                    (update-added-row)
                                    (update-existing-row)))
                                new-row))
                            [set-rows])

        add-row (uix/use-callback
                 (fn []
                   (let [row-id (.randomUUID js/crypto)
                         new-row #js {:id row-id :isNew true}]
                     (set-rows #(.concat #js [new-row] %))

                     (set-unsaved-changes {:path [:diff :add]
                                           :update-fn #(into [(->clj new-row)] %)})

                     (let [api (.-current api-ref)]
                       (.updateRows api #js [new-row])
                       (.startCellEditMode api #js {:id row-id :field "content-id"}))))
                 [api-ref set-rows])

        delete-row (uix/use-callback
                    (fn [^js row]
                      (let [row-id (.-id row)
                            delete-added-row (fn []
                                               (set-rows (fn [rows]
                                                           (.filter rows #(not= (.-id %) row-id))))

                                               (set-unsaved-changes {:path [:diff :add]
                                                                     :update-fn (fn [rows]
                                                                                  (filter #(not= (:id %) row-id) rows))}))
                            delete-existing-row (fn []
                                                  (set-unsaved-changes {:path [:diff :change]
                                                                        :update-fn #(dissoc % (keyword (str row-id)))})

                                                  (set-unsaved-changes {:path [:diff :delete]
                                                                        :update-fn #(conj % row-id)}))]
                        (if (.-isNew row)
                          (delete-added-row)
                          (delete-existing-row))))
                    [set-rows])

        ;; callback to pass an update to the data-grid.
        update-diff (uix/use-callback
                     (fn [{:keys [delete add change]}]
                       (let [new-rows (mapv (fn [row]
                                              (assoc row
                                                     :id (.randomUUID js/crypto)
                                                     :isNew true))
                                            add)]
                         (set-rows (fn [rows]
                                     (cond-> rows
                                       (seq change)
                                       (.map (fn [row]
                                               (if-let [row-change (get change (keyword (str (.-id row))))]
                                                 (util/merge-objects row (->js row-change))
                                                 row)))

                                       (seq new-rows)
                                       (->> (.concat (->js new-rows))))))

                         (set-unsaved-changes
                          {:path [:diff]
                           :update-fn (fn [diff]
                                        (let [to-delete (into (:delete diff) delete)]
                                          (cond-> diff
                                            (seq delete) (assoc :delete to-delete)
                                            (seq change) (update :change #(as-> (deep-merge % change) c
                                                                                (reduce dissoc c (map (comp keyword str) to-delete))))
                                            (seq add) (update :add #(into new-rows %)))))})))
                     [set-rows])

        discard-changes (uix/use-callback
                         (fn []
                           (let [rows-before-change (:rowsBeforeChange unsaved-changes)
                                 added-row-ids (->> (get-in unsaved-changes [:diff :add])
                                                    (map :id)
                                                    set)]
                             (set-rows (fn [rows]
                                         (cond-> rows
                                           (seq added-row-ids)
                                           (.filter #(not (contains? added-row-ids (.-id %))))

                                           (seq rows-before-change)
                                           (.map (fn [row]
                                                   (if-let [row-before-change (get rows-before-change (keyword (str (.-id row))))]
                                                     (->js row-before-change)
                                                     row))))))

                             (when on-discard
                               (on-discard))
                             (set-unsaved-changes {:action :reset})))
                         [on-discard set-rows unsaved-changes])

        save-changes (uix/use-callback
                      (fn []
                        ;; It is recommended to invalidate the query used by
                        ;; the table's `table-query-fn` after saving,
                        ;; to ensure all changes are properly reflected.
                        (when on-save
                          (on-save (:diff unsaved-changes)))
                        (set-unsaved-changes {:action :reset}))
                      [on-save unsaved-changes])

        get-row-class-name (uix/use-callback
                            (fn [^js params]
                              (let [id (.-id params)
                                    {:keys [change delete add]} (:diff unsaved-changes)
                                    row-is-changed? (get change (keyword (str id)))
                                    row-is-deleted? (contains? delete id)
                                    row-is-added? (some #(= (:id %) id) add)]
                                (cond
                                  row-is-added? "row--added"
                                  row-is-deleted? "row--deleted"
                                  row-is-changed? "row--changed"
                                  :else nil)))
                            [unsaved-changes])

        get-cell-class-name (uix/use-callback
                             (fn [^js params]
                               (let [id (.-id params)
                                     column (or (.. params -colDef -feedField)
                                                (.. params -colDef -field))
                                     cell-is-changed? (get-in unsaved-changes [:diff :change (keyword (str id)) (keyword column)])]
                                 (when cell-is-changed?
                                   "cell--changed")))
                             [unsaved-changes])

        previous-page (uix/use-ref nil)
        previous-loading (uix/use-ref nil)

        props {:processRowUpdate process-row-update
               :getRowClassName get-row-class-name
               :getCellClassName get-cell-class-name
               :sx (let [added "rgb(205 255 203)"
                         deleted "rgb(255 199 199)"
                         changed "rgb(255, 254, 176)"
                         changed-cell "rgb(252 250 46)"]
                     #js {"& .MuiDataGrid-row.row--added" #js {:backgroundColor added}
                          "& .MuiDataGrid-row.row--added .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.row--added .MuiDataGrid-cell--pinnedRight" #js {:backgroundColor added}
                          "& .MuiDataGrid-row.row--added:hover .MuiDataGrid-cell" #js {:backgroundColor added}

                          "& .MuiDataGrid-row.row--deleted" #js {:backgroundColor deleted}
                          "& .MuiDataGrid-row.row--deleted .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.row--deleted .MuiDataGrid-cell--pinnedRight" #js {:backgroundColor deleted}
                          "& .MuiDataGrid-row.row--deleted:hover .MuiDataGrid-cell" #js {:backgroundColor deleted}

                          "& .MuiDataGrid-row.row--changed" #js {:backgroundColor changed}
                          "& .MuiDataGrid-row.row--changed .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.row--changed .MuiDataGrid-cell--pinnedRight" #js {:backgroundColor changed}
                          "& .MuiDataGrid-row.row--changed:hover .MuiDataGrid-cell" #js {:backgroundColor changed}
                          "& .MuiDataGrid-cell.cell--changed" #js {:backgroundColor changed-cell}

                          "& .MuiDataGrid-row.row--added:hover, & .MuiDataGrid-row.row--changed:hover, & .MuiDataGrid-row.row--deleted:hover" #js {:filter "brightness(0.9)"}})}]

    (uix/use-effect
     (fn []
       (when on-update
         (on-update (:diff unsaved-changes))))
     [on-update unsaved-changes])

    ;; useEffect to apply changes (`change`) and additions (`add`) from `unsaved-changes-ref`
    ;; to the data-grid `rows` state.
    ;;
    ;; If the user is editing, this effect processes all inline edits by:
    ;; - Storing the original row, for each changed row.
    ;; - Merging changed rows with the existing `rows`.
    ;; - Concatenating added rows to the front of the `rows` state.
    ;;
    ;; This ensures that user edits to the content table are re-applied when
    ;; the table's resource data changes.
    ;;
    ;; The effect is triggered when `pagination-model` or `loading` changes.
    (uix/use-effect
     (fn []
       (when user-is-editing?
         (let [prev-page @previous-page
               page-changed? (not= prev-page (.-page pagination-model))
               prev-loading @previous-loading
               loading-changed? (not= loading prev-loading)]
           (reset! previous-loading loading)
           (reset! previous-page (.-page pagination-model))

           (when (and (not loading)
                      (or loading-changed? page-changed?))
             (js/setTimeout (fn []
                              (let [changed-rows (get-in unsaved-changes [:diff :change])
                                    added-rows (get-in unsaved-changes [:diff :add])]
                                (set-unsaved-changes
                                 {:path [:rowsBeforeChange]
                                  :update-fn (fn [rows-before-change]
                                               (reduce (fn [acc [id _]]
                                                         (let [row (.. api-ref -current (getRow (name id)))]
                                                           (if (and row (nil? (get acc id)))
                                                             (assoc acc id row)
                                                             acc)))
                                                       rows-before-change
                                                       changed-rows))})
                                (set-rows (fn [rows]
                                            (cond-> rows
                                              (seq changed-rows)
                                              (.map (fn [row]
                                                      (if-let [change (get changed-rows (keyword (str (.-id row))))]
                                                        (util/merge-objects row (->js change))
                                                        row)))

                                              (seq added-rows)
                                              (->> (.concat (->js added-rows))))))))
                            200)))))
     [api-ref set-rows user-is-editing? pagination-model loading unsaved-changes])

    {:save-changes save-changes
     :discard-changes discard-changes
     :update-diff update-diff
     :diff (:diff unsaved-changes)
     :add-row add-row
     :delete-row delete-row
     :props props}))

(defn remove-field [model id]
  (update model :items (fn [items] (vec (remove #(= (:id %) id) items)))))

(defui quick-select-filter-view
  [{:keys [label filter-options data-testid multiple value on-change get-values-to-render]}]
  (let [check-component (if multiple Checkbox Radio)]
    ($ Select
       {:variant "filled"
        :multiple (boolean multiple)
        :displayEmpty true
        :value (->js value)
        :onChange on-change
        :renderValue (fn [v]
                       ($ :<>
                          ($ Typography
                             {:component "span"
                              :fontWeight 500}
                             label)
                          (when-let [value (get-values-to-render v)]
                            ($ Typography
                               {:component "span"}
                               ": " (str/join ", " value)))))
        :size "small"
        :data-testid data-testid
        :sx #js {:width 140}}
       (for [{:keys [label] filter-option-value :value} filter-options]
         ($ MenuItem
            {:key filter-option-value
             :value filter-option-value
             :data-testid (str/lower-case label)}
            ($ check-component
               {:checked (if multiple
                           (contains? value filter-option-value)
                           (= value filter-option-value))})
            ($ ListItemText
               {:sx #js {:textTransform "capitalize"}}
               label))))))

(def all-option-id "quick-select-filter-all")

;; MUI requires the ID for FilterItems when you have multiple FilterItems,
;; so enforce it so it makes it simpler to replace the item vs. trying to
;; look and the field and operator.
(defui quick-select-filter
  [{:keys [label id field filter-options data-testid multiple]
    :or {multiple true}}]
  (let [[filter-model set-filter-model] (use-filter-model)
        filter-options (cond->> filter-options
                         (not multiple) (cons {:value all-option-id :label "All"}))
        filter-value->label (mc/index-by :value :label filter-options)
        filter-value (->> (->clj filter-model)
                          (:items)
                          (filter (fn [item]
                                    (= id (:id item))))
                          (first)
                          (:value))
        select-value (if multiple
                       (set filter-value)
                       (or filter-value all-option-id))
        get-values (fn [value]
                     (cond
                       multiple (seq value)
                       (= all-option-id value) nil
                       (some? value) [value]))]
    ($ quick-select-filter-view
       {:label label
        :filter-options filter-options
        :data-testid data-testid
        :multiple (boolean multiple)
        :value select-value
        :on-change (fn [^js event]
                     (let [values (get-values (-> event .-target .-value))
                           new-filter-item {:id id
                                            :field field
                                            :operator (if multiple "isAnyOf" "equals")
                                            :value (cond-> values
                                                     (not multiple) first)}]
                       (set-filter-model
                        (-> (->clj filter-model)
                            (remove-field id)
                            (cond-> (some? values) (update :items conj new-filter-item))
                            ->js))))
        :get-values-to-render (comp #(when % (map filter-value->label %)) get-values)})))

(defui daterange-filter [{:keys [label id field operator]
                          :or {operator "inTimestampRange"}}]
  (let [[filter-model set-filter-model] (use-filter-model)
        filter-value (->> (->clj filter-model)
                          (:items)
                          (filter (fn [item]
                                    (= id (:id item))))
                          (first)
                          (:value))
        empty-filter-value? (or (nil? filter-value) (some nil? filter-value))]
    ($ picker/daterange-picker
       {:value filter-value
        :sx #js {:width (if empty-filter-value? 100 195)}
        :onChange (fn [[before after]]
                    (let [new-filter-model (-> filter-model
                                               (->clj)
                                               (remove-field id)
                                               (cond-> (and before after)
                                                 (update :items conj {:id id
                                                                      :field field
                                                                      :operator operator
                                                                      :value [before after]})))]
                      (set-filter-model (->js new-filter-model))))
        :slotProps #js {:textField #js {:variant "filled"
                                        :size "small"
                                        :InputProps #js {:startAdornment (when empty-filter-value?
                                                                           ($ Typography
                                                                              {:component "span" :fontWeight 500 :sx #js {:mr 1}}
                                                                              label))}}}})))

(defui filter-checkbox-group
  [{:keys [label id field filter-options operator filter-model set-filter-model multiple]
    :or {operator "isAnyOf"
         multiple true}}]
  (let [filter-options (cond->> filter-options
                         (not multiple) (cons {:value all-option-id :label "All"}))
        filter-value (->> (->clj filter-model)
                          :items
                          (filter #(= id (:id %)))
                          first
                          :value)
        filter-field-value (if multiple
                             (set filter-value)
                             (or filter-value all-option-id))
        toggle-filter (fn [filters filter-value]
                        (if multiple
                          (if (contains? filters filter-value)
                            (disj filters filter-value)
                            (conj filters filter-value))
                          (when-not (= all-option-id filter-value)
                            filter-value)))
        component (if multiple Checkbox Radio)]
    ($ form-field {:label label}
       ($ Stack {:sx #js {:ml 0.5}}
          (for [option filter-options]
            ($ FormControlLabel
               {:key option
                :label (:label option)
                :checked (if multiple
                           (contains? filter-field-value (:value option))
                           (= filter-field-value (:value option)))
                :onChange
                (fn [_e]
                  (let [new-filter-field-value (toggle-filter filter-field-value
                                                              (:value option))
                        new-filter-model
                        (-> filter-model
                            (->clj)
                            (remove-field id)
                            (cond-> (seq new-filter-field-value)
                              (update :items conj
                                      {:id id
                                       :field field
                                       :operator (if multiple operator "equals")
                                       :value new-filter-field-value})))]
                    (set-filter-model (->js new-filter-model))))
                :control ($ component {:sx #js {:ml 0.5}})}))))))

(defui filters-list [{:keys [filters filter-model set-filter-model sx children]}]
  ($ Stack {:gap 1.5
            :sx sx}
     (for [{:keys [id] :as filter} filters]
       ($ filter-checkbox-group
          (merge filter {:key id
                         :filter-model filter-model
                         :set-filter-model set-filter-model})))
     children))

(defui filter-menu
  [{:keys [anchor-el set-anchor-el
           filters filter-model set-filter-model]}]
  (let [[temp-filter-model set-temp-filter-model] (uix/use-state filter-model)]
    ($ Popper
       {:anchorEl anchor-el
        :open (boolean anchor-el)
        :placement "bottom-start"
        :sx #js {:zIndex 1300}}
       ($ ClickAwayListener
          {:onClickAway #(set-anchor-el nil)}
          ($ Paper
             {:elevation 2
              :sx #js {:minWidth "282px"}}
             ($ filters-list
                {:filters filters
                 :filter-model temp-filter-model
                 :set-filter-model set-temp-filter-model
                 :sx #js {:padding 2}})
             ($ Stack
                {:direction "row"
                 :justifyContent "flex-end"
                 :gap 0.5
                 :sx #js {:padding 1}}
                ($ Button
                   {:variant "text"
                    :color "grey"
                    :onClick (fn []
                               (set-temp-filter-model filter-model)
                               (set-anchor-el nil))}
                   "Cancel")
                ($ Button
                   {:onClick (fn []
                               (set-filter-model temp-filter-model)
                               (set-anchor-el nil))}
                   "Apply")))))))

(defocp grid-filters-ocp :datagrid/filters
  {:elevated-z-index true}
  [{:keys [filters filter-model set-filter-model custom-filter-components]}]
  (let [[temp-filter-model set-temp-filter-model] (uix/use-state filter-model)]
    ($ ocp {:title "Filters"}
       ($ filters-list {:filters filters
                        :filter-model temp-filter-model
                        :set-filter-model set-temp-filter-model
                        :sx #js {:overflow "auto"
                                 :flex 1}}
          (for [{:keys [id component] :as props} custom-filter-components]
            ($ component (merge (dissoc props :id :component)
                                {:key id
                                 :filter-model temp-filter-model
                                 :set-filter-model set-temp-filter-model}))))
       ($ Stack {:direction "row"
                 :justifyContent "flex-end"
                 :gap 0.5}
          ($ Button
             {:variant "text"
              :color "grey"
              :onClick (fn []
                         (set-temp-filter-model filter-model)
                         (rf/dispatch [:ocp/close]))}
             "Cancel")
          ($ Button
             {:onClick (fn []
                         (set-filter-model temp-filter-model)
                         (rf/dispatch [:ocp/close]))}
             "Apply")))))

(defui filters-button
  [{:keys [filters variant custom-filter-components]}]
  (let [[anchor-el set-anchor-el] (uix/use-state nil)

        [filter-model set-filter-model] (use-filter-model)
        filter-count (count (.-items filter-model))

        variant (or variant
                    (if (> (count filters) 2)
                      :ocp
                      :menu))
        on-click (uix/use-callback
                  (fn [e]
                    (case variant
                      :menu
                      (set-anchor-el (fn [anchor-el]
                                       (if anchor-el
                                         nil
                                         (.-currentTarget e))))

                      :ocp
                      (rf/dispatch
                       [:ocp/open {:id :datagrid/filters
                                   :props {:filters filters
                                           :custom-filter-components custom-filter-components
                                           :filter-model filter-model
                                           :set-filter-model set-filter-model}}])))
                  [variant filters filter-model set-filter-model custom-filter-components])]
    ($ :<>
       ($ Button
          {:variant "outlined"
           :color "grey"
           :onClick on-click
           :startIcon ($ Filter)
           :sx (spread-obj #js {:borderColor "grey.50"
                                :color "grey.10"
                                "&:hover" #js {:backgroundColor "grey.80"
                                               :borderColor "grey.30"}}
                           (when (boolean anchor-el)
                             #js {:backgroundColor "grey.70"
                                  :borderColor "grey.20"
                                  :color "grey.100"}))}
          (str "Filters"
               (when (pos? filter-count)
                 (str " (" filter-count ")"))))
       (when (= variant :menu)
         ($ filter-menu
            {:anchor-el anchor-el
             :set-anchor-el set-anchor-el
             :filters filters
             :filter-model filter-model
             :set-filter-model set-filter-model})))))

(defn hide-root-group-row [groupNode] (when-not (nil? groupNode) "inline"))

(defn first-aggregator
  "Aggregator for taking the first value in the group. Example usage:
  ```clojure
  :aggregationFunctions #js {:first #js {:apply first-label-path}}
  :initialState #js {:aggregation #js {:model #js {:column-field \"first\"}}}
  ```"
  [^js params]
  (first (.-values params)))

(def coll-contains-some-operator
  #js {:label "Contains"
       :value "collContainsSome"
       :getApplyFilterFn (fn [^js filter-item]
                           (when (and (.-field filter-item) (.-value filter-item) (.-operator filter-item))
                             (fn [value]
                               (some (set value) (->clj (.-value filter-item))))))})

(defn render-only-group-rows [f]
  (fn [^js params]
    (when (isAutogeneratedRow (.-row params))
      (if (some? f)
        (f params)
        (.-value params)))))

(defn render-only-child-rows [f]
  (fn [^js params]
    (when-not (isAutogeneratedRow (.-row params))
      (if (some? f)
        (f params)
        (.-value params)))))

(def hover-action-sx
  {".hover-action-cell > *" #js {:opacity 0
                                 :transition "opacity 100ms linear"}
   "& .MuiDataGrid-row:hover .hover-action-cell > *, & .Mui-selected .hover-action-cell > *"
   #js {:opacity 1}})

(def actions-column-sx
  {".MuiDataGrid-actionsCell svg" #js {:opacity 0
                                       :transition "opacity 100ms linear"}
   "& .MuiDataGrid-row:hover .MuiDataGrid-actionsCell svg, & .Mui-selected .MuiDataGrid-actionsCell svg"
   #js {:opacity 1}})

(defn get-cell-renderer [component]
  (uix/as-react
   (fn [props]
     ($ component props))))
